import { useState, useCallback, useEffect, useMemo } from 'react'
import { ChatHeader } from './ChatHeader'
import { ChatMessages } from './ChatMessages'
import { ChatInput } from './ChatInput'
import { ChatSessionList } from './ChatSessionList'
import { ChatScopeSelector } from './ChatScopeSelector'
import { ContextHealthIndicator } from './ContextHealthIndicator'
import type { ScopeOption } from './ChatScopeSelector'
import { useChatContext } from '../../hooks/useChatContext'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

interface MessageAttachment {
  name: string
  type: string
  size: number
  previewUrl?: string
}

interface Message {
  id?: string
  role: 'user' | 'assistant' | 'system'
  content: string
  tool_calls?: any[]
  attachments?: MessageAttachment[]
}

interface Session {
  id: string
  title: string | null
  scope: string
  message_count: number
  last_message_at: string | null
  created_at: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
}

// Map auto-detected context scope to icon string keys used by ChatScopeSelector
const SCOPE_ICON_MAP: Record<string, string> = {
  app: 'globe',
  leads: 'list-todo',
  lead: 'user',
  'csp-customers': 'building-2',
  'csp-customer': 'building-2',
  'it-budgets': 'dollar-sign',
  'gitlab-features': 'sparkles',
  settings: 'globe',
  'kg-portfolio': 'network',
  'kg-customer': 'network',
  'kg-territory': 'network',
  'kg-ae': 'network',
}

const SCOPE_LABELS: Record<string, string> = {
  app: 'General',
  leads: 'All Leads',
  lead: 'Lead',
  'csp-customers': 'All Customers',
  'csp-customer': 'Customer',
  'it-budgets': 'IT Budgets',
  'gitlab-features': 'GitLab Features',
  settings: 'Settings',
  'kg-portfolio': 'Knowledge Graph',
  'kg-customer': 'KG — Customer',
  'kg-territory': 'KG — Territory',
  'kg-ae': 'KG — AE',
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  app: 'No specific entity — general GitLab & compliance Q&A',
  leads: 'Aggregate lead data, batch analysis, scoring trends',
  lead: 'Lead context, enrichment data, scoring details',
  'csp-customers': 'All customer data, pipeline analysis',
  'csp-customer': 'Opportunities, contacts, mission context, documents',
  'it-budgets': 'Federal & SLED IT spending data and analysis',
  'gitlab-features': 'Feature catalog, comparisons, capability mapping',
  settings: 'Application settings and configuration',
  'kg-portfolio': 'Cross-portfolio graph: tools, pain points, value drivers, product demand',
  'kg-customer': 'Customer element graph: pain points, objectives, features, risks, tools',
  'kg-territory': 'Territory aggregate: common tools, shared pain points, product demand',
  'kg-ae': 'AE portfolio graph: customer patterns, tool overlap, risk landscape',
}

function contextToScopeOption(ctx: ReturnType<typeof useChatContext>, isCurrentPage = false): ScopeOption {
  const label = ctx.entityName
    ? `${SCOPE_LABELS[ctx.scope] || ctx.scope}: ${ctx.entityName}`
    : SCOPE_LABELS[ctx.scope] || ctx.scope

  return {
    scope: ctx.scope,
    entityType: ctx.entityType,
    entityId: ctx.entityId,
    entityName: ctx.entityName,
    label,
    description: SCOPE_DESCRIPTIONS[ctx.scope] || '',
    icon: SCOPE_ICON_MAP[ctx.scope] || 'globe',
    isCurrentPage,
  }
}

function buildScopeOptions(autoContext: ReturnType<typeof useChatContext>): ScopeOption[] {
  const options: ScopeOption[] = [
    {
      scope: 'app',
      label: 'General',
      description: 'No specific entity — general GitLab & compliance Q&A',
      icon: 'globe',
    },
  ]

  if (autoContext.scope !== 'app') {
    options.push(contextToScopeOption(autoContext, true))
  }

  const fixedScopes: ScopeOption[] = [
    { scope: 'leads', label: 'All Leads', description: 'Aggregate lead data, batch analysis, scoring trends', icon: 'list-todo' },
    { scope: 'it-budgets', label: 'IT Budgets', description: 'Federal & SLED IT spending data and analysis', icon: 'dollar-sign' },
    { scope: 'gitlab-features', label: 'GitLab Features', description: 'Feature catalog, comparisons, capability mapping', icon: 'sparkles' },
  ]

  for (const fs of fixedScopes) {
    if (!options.some(o => o.scope === fs.scope)) {
      options.push(fs)
    }
  }

  return options
}

export function ChatPanel({ isOpen, onClose }: Props) {
  const context = useChatContext()
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null)
  const [toolStatus, setToolStatus] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showScopeSelector, setShowScopeSelector] = useState(false)
  const [useMemory, setUseMemory] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(true)
  const [contextBudget, setContextBudget] = useState<{ utilization: number; totalTokens: number; tiers?: string } | null>(null)

  // Selected scope defaults to auto-detected context
  const [selectedScope, setSelectedScope] = useState<ScopeOption>(() =>
    contextToScopeOption(context)
  )

  // Update selected scope when auto-detected context changes (page navigation)
  useEffect(() => {
    setSelectedScope(contextToScopeOption(context, true))
  }, [context.scope, context.entityId])

  // Build scope options for the selector
  const scopeOptions = useMemo(() => buildScopeOptions(context), [context])

  // Load sessions when panel opens
  useEffect(() => {
    if (isOpen) {
      loadSessions()
    }
  }, [isOpen])

  // Load messages when session changes
  useEffect(() => {
    if (sessionId) {
      loadMessages(sessionId)
    }
  }, [sessionId])

  const loadSessions = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/chat/sessions?scope=${selectedScope.scope}${selectedScope.entityId ? `&entityId=${selectedScope.entityId}` : ''}`
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setSessions(data.data?.sessions || [])
      setIsConnected(true)
    } catch (err: any) {
      console.error('Failed to load sessions:', err)
      setIsConnected(false)
    }
  }

  const loadMessages = async (sid: string) => {
    setIsLoadingMessages(true)
    try {
      const res = await fetch(`${API_BASE}/api/chat/sessions/${sid}/messages`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setMessages(data.data?.messages || [])
      setError(null)
      setIsConnected(true)
    } catch (err: any) {
      console.error('Failed to load messages:', err)
      setError('Failed to load message history')
      setIsConnected(false)
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const createSession = async (): Promise<string> => {
    const res = await fetch(`${API_BASE}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scope: selectedScope.scope,
        entityType: selectedScope.entityType,
        entityId: selectedScope.entityId,
        entityName: selectedScope.entityName,
        useMemory,
      }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const session = data.data?.session
    setSessionId(session.id)
    return session.id
  }

  const handleSend = useCallback(async (message: string, attachments?: File[]) => {
    setIsLoading(true)
    setStreamingMessage('')
    setToolStatus(null)
    setError(null)

    // Add user message immediately
    const messageAttachments = attachments?.map(f => ({
      name: f.name,
      type: f.type,
      size: f.size,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }))
    setMessages(prev => [...prev, { role: 'user', content: message, attachments: messageAttachments }])

    try {
      // Create session if needed
      let sid = sessionId
      if (!sid) {
        sid = await createSession()
      }

      // Stream response — use FormData when attachments present, JSON otherwise
      let res: Response
      if (attachments?.length) {
        const formData = new FormData()
        formData.append('message', message)
        formData.append('scope', selectedScope.scope)
        if (selectedScope.entityType) formData.append('entityType', selectedScope.entityType)
        if (selectedScope.entityId) formData.append('entityId', selectedScope.entityId)
        if (selectedScope.entityName) formData.append('entityName', selectedScope.entityName)
        formData.append('useMemory', String(useMemory))
        for (const file of attachments) {
          formData.append('files', file)
        }
        res = await fetch(`${API_BASE}/api/chat/sessions/${sid}/messages`, {
          method: 'POST',
          body: formData,
        })
      } else {
        res = await fetch(`${API_BASE}/api/chat/sessions/${sid}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message,
            scope: selectedScope.scope,
            entityType: selectedScope.entityType,
            entityId: selectedScope.entityId,
            entityName: selectedScope.entityName,
            useMemory,
          }),
        })
      }

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`)
      }

      setIsConnected(true)
      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let accumulatedText = ''
      let toolCallsUsed: any[] = []

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const payload = line.slice(6).trim()
            if (payload === '[DONE]') continue

            try {
              const event = JSON.parse(payload)

              if (event.type === 'text') {
                accumulatedText += event.text
                setStreamingMessage(accumulatedText)
              } else if (event.type === 'tool_status') {
                if (event.status === 'executing') {
                  setToolStatus(event.tool)
                } else {
                  setToolStatus(null)
                  toolCallsUsed.push({ name: event.tool })
                }
              } else if (event.type === 'context_budget') {
                setContextBudget({
                  utilization: event.utilization,
                  totalTokens: event.totalTokens,
                  tiers: event.tiers,
                })
              } else if (event.type === 'observation_masked') {
                // Tool result was summarized to save context space
                console.log(`[Context] Masked ${event.toolName}: saved ${event.saved} tokens`)
              } else if (event.type === 'done') {
                // Stream complete — capture final utilization if provided
                if (event.context_utilization != null) {
                  setContextBudget(prev => prev ? { ...prev, utilization: event.context_utilization } : prev)
                }
              } else if (event.type === 'error') {
                accumulatedText += `\n\n[Error: ${event.message}]`
                setStreamingMessage(accumulatedText)
              }
            } catch {
              // Skip unparseable lines
            }
          }
        }
      }

      // Finalize: add assistant message to state
      setStreamingMessage(null)
      setToolStatus(null)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: accumulatedText,
        tool_calls: toolCallsUsed.length > 0 ? toolCallsUsed : undefined,
      }])

      // Refresh sessions list
      loadSessions()

    } catch (err: any) {
      setStreamingMessage(null)
      setToolStatus(null)
      setIsConnected(false)
      const errorMessage = err.message || 'Failed to get response'
      setError(errorMessage)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${errorMessage}`,
      }])
    } finally {
      setIsLoading(false)
    }
  }, [sessionId, selectedScope, useMemory])

  const handleRetry = useCallback(() => {
    setError(null)
    // Find last user message and retry it
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')
    if (lastUserMsg) {
      // Remove the failed assistant message
      setMessages(prev => {
        const copy = [...prev]
        if (copy.length > 0 && copy[copy.length - 1]!.role === 'assistant') {
          copy.pop()
        }
        // Also remove the user message since handleSend will re-add it
        if (copy.length > 0 && copy[copy.length - 1]!.role === 'user') {
          copy.pop()
        }
        return copy
      })
      handleSend(lastUserMsg.content)
    }
  }, [messages, handleSend])

  const handleNewChat = useCallback(() => {
    setSessionId(null)
    setMessages([])
    setStreamingMessage(null)
    setShowHistory(false)
    setShowScopeSelector(false)
    setContextBudget(null)
    setError(null)
  }, [])

  const handleSelectSession = useCallback((id: string) => {
    setSessionId(id)
    setShowHistory(false)
    setShowScopeSelector(false)
    setError(null)
  }, [])

  const handleScopeChange = useCallback((scope: ScopeOption) => {
    setSelectedScope(scope)
    setShowScopeSelector(false)

    // If there's an active session, reset it (start new one)
    if (sessionId) {
      setSessionId(null)
      setMessages([])
      setStreamingMessage(null)
      setError(null)
    }
  }, [sessionId])

  return (
    <div
      className={`fixed top-[105px] right-0 bottom-0 w-[440px] z-[55] bg-white shadow-2xl border-l-2 border-primary-200 rounded-l-xl flex flex-col overflow-hidden transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      {showHistory ? (
        <ChatSessionList
          sessions={sessions}
          currentSessionId={sessionId}
          onSelect={handleSelectSession}
          onNew={handleNewChat}
          onClose={() => setShowHistory(false)}
          onDelete={(id) => {
            setSessions(prev => prev.filter(s => s.id !== id))
            if (sessionId === id) {
              setSessionId(null)
              setMessages([])
            }
          }}
        />
      ) : (
        <>
          <ChatHeader
            context={context}
            selectedScope={selectedScope}
            messages={messages}
            onClose={onClose}
            onNewChat={handleNewChat}
            onToggleHistory={() => setShowHistory(true)}
            onToggleScopeSelector={() => setShowScopeSelector(!showScopeSelector)}
            useMemory={useMemory}
            onToggleMemory={() => setUseMemory(!useMemory)}
            isConnected={isConnected}
          />
          <ContextHealthIndicator
            utilization={contextBudget?.utilization ?? null}
            totalTokens={contextBudget?.totalTokens ?? null}
            tiers={contextBudget?.tiers}
          />
          {showScopeSelector ? (
            <ChatScopeSelector
              options={scopeOptions}
              selectedScope={selectedScope.scope}
              onSelect={handleScopeChange}
              onClose={() => setShowScopeSelector(false)}
            />
          ) : (
            <ChatMessages
              messages={messages}
              streamingMessage={streamingMessage}
              toolStatus={toolStatus}
              error={error}
              isLoadingMessages={isLoadingMessages}
              onRetry={handleRetry}
              onSendSuggestion={handleSend}
              selectedScope={selectedScope}
              onShowScopeSelector={() => setShowScopeSelector(true)}
            />
          )}
          <ChatInput onSend={handleSend} disabled={isLoading} />
        </>
      )}
    </div>
  )
}
